import {
    Color,
    Hair,
    HairColors,
    Pants,
    PantsColors,
    RaceColors,
    Sex,
    SexHair,
    SexPants,
    SexShirts,
    SexShoes,
    SexTypes,
    Shirt,
    ShirtColors,
    Shoe,
    ShoeColors,
} from "game-common/character/character"
import {
    ItemClass,
    ItemTypeMeta,
    addInventoryItem,
    locateInventoryActiveItem,
    locateInventoryActiveItemObject,
    removeInventoryItem,
} from "game-common/item/item"
import { emberFacialHairOrder } from "game-common/ember_sprite/model"
import { itemDisplayName } from "game-common/item/display_name"
import { Entity } from "game-common/models"
import { randomId } from "game-common/util"

import type { WheelIcon } from "../command_wheel_icons"
import { SwatchFamily, prettyLabel, swatchFor } from "./theme"

// The character-creation screen's model of "what can I change, and what are my choices?".
//
// Every trait answers with the same shape -- one or two palettes of options -- so the picker only
// ever has to draw a grid of labelled chips or a grid of colour swatches, whichever a section asks
// for. Pure: no pixi, no renderer, so both halves (the options offered, and what picking one does to
// the entity) are unit-tested.

export const NONE = "none"

// Eyes and nose aren't offered: the character art no longer draws them as separate choices. The beard is
// offered only to a male build (traitsFor).
export const TRAIT_IDS = ["sex", "skin", "hair", "beard", "shirt", "pants", "shoes"] as const

export type TraitId = (typeof TRAIT_IDS)[number]

export type SectionKind = "style" | "color"

export interface TraitOption {
    value: string
    label: string
    // Present on colour options: the chip is drawn as this colour rather than as a label.
    swatch?: number
}

export interface TraitSection {
    kind: SectionKind
    title: string
    options: TraitOption[]
    selected: string
}

export interface TraitView {
    id: TraitId
    label: string
    // Drawn from the game's own glyph set (the command wheel's), not an emoji.
    icon: WheelIcon
    // The current choice, shown small and dim beside the trait's name.
    summary: string
    // Colour of the trait's current tint, for the dot on the trait row (absent for style-only traits).
    swatch?: number
    sections: TraitSection[]
}

const TRAIT_META: Record<TraitId, { label: string; icon: WheelIcon }> = {
    sex: { label: "Build", icon: "figure" },
    skin: { label: "Skin", icon: "hand" },
    hair: { label: "Hair", icon: "hair" },
    beard: { label: "Beard", icon: "hair" },
    shirt: { label: "Shirt", icon: "tunic" },
    pants: { label: "Legs", icon: "trousers" },
    shoes: { label: "Feet", icon: "boot" },
}

const GARMENT_CLASS: Partial<Record<TraitId, ItemClass>> = {
    shirt: "shirt",
    pants: "pants",
    shoes: "shoes",
}

// Colours a given garment type can be painted.
const garmentColors = (itemClass: ItemClass, type?: string): Color[] => {
    if (!type || type === NONE) {
        return []
    }
    if (itemClass === "shirt") {
        const colors = ShirtColors[type as Shirt] || []
        // The forge's generated human bodies can dye a plain longsleeve grey even though the
        // legacy male LPC sheet has no grey variant.
        return type === "longsleeve" ? Array.from(new Set([...colors, "gray"])) : colors
    }
    if (itemClass === "pants") {
        return PantsColors[type as Pants] || []
    }
    if (itemClass === "shoes") {
        return ShoeColors[type as Shoe] || []
    }
    return []
}

// Types a garment can be, for this character's sex. Shirts and shoes may be left off; trousers of
// some kind are not optional.
const garmentTypes = (itemClass: ItemClass, sex: Sex): string[] => {
    if (itemClass === "shirt") {
        return [...SexShirts[sex], NONE]
    }
    if (itemClass === "pants") {
        return [...SexPants[sex]]
    }
    if (itemClass === "shoes") {
        return [...SexShoes[sex], NONE]
    }
    return []
}

/** The traits a character of this build sets: a beard only for a man. */
export const traitsFor = (sex: Sex): TraitId[] => TRAIT_IDS.filter(id => id !== "beard" || sex === "male")

/** Every facial hair a man can grow, clean-shaven first, then content's styles lightest to heaviest. */
const beardStyles = (): string[] => [NONE, ...emberFacialHairOrder()]
const beardLabel = (value?: string): string => (!value || value === NONE ? "Clean-shaven" : prettyLabel(value))

/**
 * The colours hair can take. A beard grows in the hair colour, so a bald man with a beard is still offered
 * them: the colours every hairstyle of his build comes in.
 */
const hairColorsFor = (hair: Hair | undefined, sex: Sex): Color[] =>
    hair ? (HairColors[hair] as Color[]) ?? [] : Array.from(new Set(SexHair[sex].flatMap(style => (HairColors[style] as Color[]) ?? [])))

const styleSection = (title: string, values: string[], selected?: string, label: (value?: string) => string = prettyLabel): TraitSection => ({
    kind: "style",
    title,
    options: values.map(value => ({ value, label: label(value) })),
    selected: selected || NONE,
})

const colorSection = (
    title: string,
    family: SwatchFamily,
    values: Color[],
    selected?: Color,
): TraitSection => ({
    kind: "color",
    title,
    options: values.map(value => ({ value, label: prettyLabel(value), swatch: swatchFor(family, value) })),
    selected: selected || "",
})

// What the trait list shows for one trait, and what its palette offers.
export const describeTrait = (entity: Entity, id: TraitId): TraitView => {
    const { label, icon } = TRAIT_META[id]
    const appearance = entity.appearance
    const sex = appearance.sex
    const race = appearance.race

    const view = (summary: string, sections: TraitSection[], swatch?: number): TraitView => ({
        id,
        label,
        icon,
        summary,
        swatch,
        sections,
    })

    if (id === "sex") {
        return view(prettyLabel(sex), [styleSection("Build", [...SexTypes], sex)])
    }

    if (id === "skin") {
        const colors = (RaceColors[race] || []) as Color[]
        return view(
            prettyLabel(appearance.bodyColor),
            [colorSection("Skin tone", "skin", colors, appearance.bodyColor)],
            swatchFor("skin", appearance.bodyColor),
        )
    }

    if (id === "hair") {
        const styles = [NONE, ...SexHair[sex]]
        const sections = [styleSection("Style", styles, appearance.hair || NONE)]
        // Hair, or a beard, has a colour.
        const coloured = !!appearance.hair || !!appearance.facialHair
        if (coloured) {
            sections.push(colorSection("Colour", "hair", hairColorsFor(appearance.hair, sex), appearance.hairColor))
        }
        return view(
            prettyLabel(appearance.hair || NONE),
            sections,
            coloured ? swatchFor("hair", appearance.hairColor) : undefined,
        )
    }

    if (id === "beard") {
        const beard = appearance.facialHair || NONE
        return view(beardLabel(beard), [styleSection("Style", beardStyles(), beard, beardLabel)])
    }

    // shirt / pants / shoes
    const itemClass = GARMENT_CLASS[id]
    const worn = locateInventoryActiveItem(entity.inventory, itemClass)
    const type = worn?.type
    // A garment is an item, so it is called what the pack calls it.
    const garmentLabel = (value?: string) => (value && value !== NONE ? itemDisplayName(value) : prettyLabel(value))
    const sections = [styleSection("Cut", garmentTypes(itemClass, sex), type || NONE, garmentLabel)]
    const colors = garmentColors(itemClass, type)
    if (colors.length > 0) {
        sections.push(colorSection("Colour", "cloth", colors, worn?.color))
    }
    return view(
        garmentLabel(type || NONE),
        sections,
        worn?.color ? swatchFor("cloth", worn.color) : undefined,
    )
}

export const describeTraits = (entity: Entity): TraitView[] => traitsFor(entity.appearance.sex).map(id => describeTrait(entity, id))

// Swap what the character wears in one slot. Removing first keeps a single active item per class,
// and both helpers refresh the inventory-appearance hash the server reads.
const equip = (entity: Entity, itemClass: ItemClass, type?: string, preferredColor?: Color) => {
    const existing = locateInventoryActiveItemObject(entity.inventory, itemClass)
    if (existing) {
        removeInventoryItem(entity, existing.id)
    }
    if (!type || type === NONE) {
        return
    }
    const colors = garmentColors(itemClass, type)
    const color = preferredColor && colors.includes(preferredColor) ? preferredColor : colors[0]
    const itemTypeMeta: ItemTypeMeta = { type: type as any, class: itemClass, color }
    addInventoryItem(entity, {
        id: randomId(),
        itemType: "none",
        itemTypeMeta,
        quantity: 1,
        active: true,
    })
}

// Sex decides which hairstyles and which cuts of clothing exist, so a switch has to re-home anything
// the new body can't wear -- keeping the colour wherever the replacement supports it.
export const reconcileForSex = (entity: Entity) => {
    const appearance = entity.appearance
    const sex = appearance.sex

    if (appearance.hair && !SexHair[sex].includes(appearance.hair)) {
        appearance.hair = SexHair[sex][0]
    }
    // Only a man grows a beard.
    if (!traitsFor(sex).includes("beard")) {
        delete appearance.facialHair
    }

    ;(["shirt", "pants", "shoes"] as ItemClass[]).forEach(itemClass => {
        const worn = locateInventoryActiveItem(entity.inventory, itemClass)
        const allowed = garmentTypes(itemClass, sex)
        if (!worn) {
            // Trousers are the one slot nobody goes without.
            if (itemClass === "pants") {
                equip(entity, itemClass, allowed[0])
            }
            return
        }
        if (!allowed.includes(worn.type)) {
            equip(entity, itemClass, allowed[0], worn.color)
        }
    })
}

// Apply one palette click. Mutates the (builder-local) entity in place.
export const applyTraitChoice = (entity: Entity, id: TraitId, kind: SectionKind, value: string) => {
    const appearance = entity.appearance

    if (id === "sex") {
        appearance.sex = value as Sex
        reconcileForSex(entity)
        return
    }

    if (id === "skin") {
        appearance.bodyColor = value as Color
        // The nose is a separate sheet tinted to match the body; left alone it floats as a different
        // colour patch on the face.
        appearance.noseColor = value as Color
        return
    }

    if (id === "hair") {
        if (kind === "color") {
            appearance.hairColor = value as Color
            return
        }
        if (value === NONE) {
            appearance.hair = undefined
            return
        }
        appearance.hair = value as Hair
        const colors = HairColors[appearance.hair] as Color[]
        if (!colors?.includes(appearance.hairColor)) {
            appearance.hairColor = colors?.[0]
        }
        return
    }

    if (id === "beard") {
        if (value === NONE) {
            delete appearance.facialHair
            return
        }
        appearance.facialHair = value
        // A beard grows in the hair colour: a bald man who has none yet takes the first he is offered.
        if (!appearance.hairColor) {
            appearance.hairColor = hairColorsFor(appearance.hair, appearance.sex)[0]
        }
        return
    }

    const itemClass = GARMENT_CLASS[id]
    const worn = locateInventoryActiveItem(entity.inventory, itemClass)
    if (kind === "color") {
        equip(entity, itemClass, worn?.type, value as Color)
        return
    }
    equip(entity, itemClass, value, worn?.color)
}
